import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);

/**
 * 格式化日期
 */
export function formatDate(
  date: dayjs.ConfigType,
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string {
  return dayjs(date).format(format);
}

/**
 * 获取相对时间
 */
export function getRelativeTime(
  date: dayjs.ConfigType,
  locale: string = 'zh-cn'
): string {
  return dayjs(date).locale(locale).fromNow();
}

/**
 * 获取时间差
 */
export function getTimeDiff(
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType,
  unit: dayjs.UnitType = 'millisecond'
): number {
  return dayjs(endDate).diff(dayjs(startDate), unit);
}

/**
 * 添加时间
 */
export function addTime(
  date: dayjs.ConfigType,
  amount: number,
  unit: dayjs.ManipulateType
): dayjs.Dayjs {
  return dayjs(date).add(amount, unit);
}

/**
 * 减去时间
 */
export function subtractTime(
  date: dayjs.ConfigType,
  amount: number,
  unit: dayjs.ManipulateType
): dayjs.Dayjs {
  return dayjs(date).subtract(amount, unit);
}

/**
 * 获取时间范围的开始时间
 */
export function startOf(
  date: dayjs.ConfigType,
  unit: dayjs.OpUnitType
): dayjs.Dayjs {
  return dayjs(date).startOf(unit);
}

/**
 * 获取时间范围的结束时间
 */
export function endOf(
  date: dayjs.ConfigType,
  unit: dayjs.OpUnitType
): dayjs.Dayjs {
  return dayjs(date).endOf(unit);
}

/**
 * 检查是否为同一天
 */
export function isSameDay(
  date1: dayjs.ConfigType,
  date2: dayjs.ConfigType
): boolean {
  return dayjs(date1).isSame(dayjs(date2), 'day');
}

/**
 * 检查是否为今天
 */
export function isToday(date: dayjs.ConfigType): boolean {
  return isSameDay(date, new Date());
}

/**
 * 检查是否为昨天
 */
export function isYesterday(date: dayjs.ConfigType): boolean {
  return isSameDay(date, dayjs().subtract(1, 'day'));
}

/**
 * 检查是否为明天
 */
export function isTomorrow(date: dayjs.ConfigType): boolean {
  return isSameDay(date, dayjs().add(1, 'day'));
}

/**
 * 检查是否为本周
 */
export function isThisWeek(date: dayjs.ConfigType): boolean {
  return dayjs(date).isSame(dayjs(), 'week');
}

/**
 * 检查是否为本月
 */
export function isThisMonth(date: dayjs.ConfigType): boolean {
  return dayjs(date).isSame(dayjs(), 'month');
}

/**
 * 检查是否为本年
 */
export function isThisYear(date: dayjs.ConfigType): boolean {
  return dayjs(date).isSame(dayjs(), 'year');
}

/**
 * 获取年龄
 */
export function getAge(birthDate: dayjs.ConfigType): number {
  return dayjs().diff(dayjs(birthDate), 'year');
}

/**
 * 获取工作日
 */
export function getWorkdays(
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType
): number {
  let count = 0;
  let current = dayjs(startDate);
  const end = dayjs(endDate);

  while (current.isBefore(end) || current.isSame(end, 'day')) {
    const dayOfWeek = current.day();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // 不是周末
      count++;
    }
    current = current.add(1, 'day');
  }

  return count;
}

/**
 * 获取月份的天数
 */
export function getDaysInMonth(date: dayjs.ConfigType): number {
  return dayjs(date).daysInMonth();
}

/**
 * 获取季度
 */
export function getQuarter(date: dayjs.ConfigType): number {
  return Math.ceil((dayjs(date).month() + 1) / 3);
}

/**
 * 获取周数
 */
export function getWeekOfYear(date: dayjs.ConfigType): number {
  return dayjs(date).week();
}

/**
 * 转换时区
 */
export function convertTimezone(
  date: dayjs.ConfigType,
  timezone: string
): dayjs.Dayjs {
  return dayjs(date).tz(timezone);
}

/**
 * 获取UTC时间
 */
export function toUTC(date: dayjs.ConfigType): dayjs.Dayjs {
  return dayjs(date).utc();
}

/**
 * 从UTC时间转换为本地时间
 */
export function fromUTC(date: dayjs.ConfigType): dayjs.Dayjs {
  return dayjs.utc(date).local();
}

/**
 * 获取时间戳
 */
export function getTimestamp(date: dayjs.ConfigType = new Date()): number {
  return dayjs(date).valueOf();
}

/**
 * 从时间戳创建日期
 */
export function fromTimestamp(timestamp: number): dayjs.Dayjs {
  return dayjs(timestamp);
}

/**
 * 检查日期是否有效
 */
export function isValidDate(date: any): boolean {
  return dayjs(date).isValid();
}

/**
 * 获取日期范围
 */
export function getDateRange(
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType,
  unit: dayjs.ManipulateType = 'day'
): dayjs.Dayjs[] {
  const dates: dayjs.Dayjs[] = [];
  let current = dayjs(startDate);
  const end = dayjs(endDate);

  while (current.isBefore(end) || current.isSame(end, unit as dayjs.UnitType)) {
    dates.push(current);
    current = current.add(1, unit);
  }

  return dates;
}
